import { DataGrid, type DataGridColumn, formatCurrency, Link, Typography } from 'ui';

import { PricedSupplySignal } from '~/price-calculator/types';

const displayOnlyOn4Lines = {
  maxHeight: '6em',
  overflowY: 'scroll',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
};

const columns: DataGridColumn<PricedSupplySignal>[] = [
  {
    columnSize: 'small',
    field: 'hubspot_url',
    headerName: 'HubSpot URL',
    renderCell: (params) => (
      <Link href={params.row.hubspot_url} target="_blank" rel="noreferrer noopener">
        Link
      </Link>
    ),
  },
  {
    field: 'seller',
    headerName: 'Seller',
    valueGetter: (params) => params.row.seller.name,
  },
  {
    field: 'material_spec',
    headerName: 'Material',
    valueGetter: (params) => {
      const title = params.row.material_spec?.title || '';
      const grade = params.row.material_grade?.name || '';
      return `${title} (${grade})`;
    },
  },
  {
    columnSize: 'small',
    field: 'available_tonnes',
    headerName: 'Available tonnes',
    valueGetter: (params) => params.row.tonnes,
  },
  {
    columnSize: 'small',
    field: 'tonnes_per_load',
    headerName: 'Payload',
    valueGetter: (params) => params.row.tonnes_per_load,
  },
  {
    columnSize: 'large',
    field: 'route',
    headerName: 'Route',
    renderCell: (params) => {
      const port_of_loading = params.row.price_details?.used_port_of_loading || params.row.port_of_loading;
      const port_of_discharge = params.row.price_details?.used_port_of_discharge || params.row.port_of_discharge;
      const { origin_site, destination_site } = params.row.price_details || {};
      const route = [
        origin_site?.full_address,
        port_of_loading?.name,
        port_of_discharge?.name,
        destination_site?.full_address,
      ]
        .filter(Boolean)
        .join(' > ');
      return <Typography sx={displayOnlyOn4Lines}>{route}</Typography>;
    },
  },
  {
    field: 'total_transport_cost',
    headerName: 'Total transport cost',
    valueGetter: (params) =>
      params.row.price_details?.total_transport_cost &&
      params.row.price_details?.transport_cost_currency &&
      formatCurrency(params.row.price_details.total_transport_cost, {
        currencyCode: params.row.price_details.transport_cost_currency,
      }),
  },
  {
    columnSize: 'small',
    field: 'seller_incoterm',
    headerName: 'Seller incoterm',
  },
  {
    field: 'seller_material_price',
    headerName: 'Seller material price /t',
    valueGetter: (params) =>
      formatCurrency(params.row.seller_price_per_tonne, {
        currencyCode: params.row.seller_currency,
      }),
  },
  {
    field: 'buyer_material_price',
    headerName: 'Buyer material price /t',
    valueGetter: (params) =>
      params.row.price_details?.buyer_price_per_tonne &&
      params.row.price_details?.buyer_currency &&
      formatCurrency(params.row.price_details.buyer_price_per_tonne, {
        currencyCode: params.row.price_details.buyer_currency,
      }),
  },
  {
    field: 'calculator_error',
    headerName: 'Calculator error',
    renderCell: (params) => {
      return <Typography sx={displayOnlyOn4Lines}>{params.row.price_calculator_error}</Typography>;
    },
  },
];

interface Props {
  pricedSupplySignals?: PricedSupplySignal[];
}

export const PricedSupplySignalsGrid = ({ pricedSupplySignals }: Props) => {
  const rows = pricedSupplySignals ?? [];

  if (rows.length === 0) {
    return <Typography align="center">No supply signals to display</Typography>;
  }

  return (
    <DataGrid
      rowSelection={false}
      columns={columns}
      density="compact"
      rows={rows}
      getRowHeight={() => 'auto'}
      getRowId={(row) => row.id}
      sx={{ fontSize: 'medium', left: '50%', marginRight: '5%', transform: 'translate(-50%,0%)', width: '95vw' }}
    />
  );
};
